<template>
  <div v-click-outside="close" class="c-week-picker">
    <!-- Trigger -->
    <div
      class="trigger"
      :class="{ 'is-open': isOpen, 'has-value': !!selectedMon }"
      @click="toggle"
    >
      <span class="trigger-text">
        {{ selectedMon ? triggerLabel : placeholder }}
      </span>
      <svg class="icon-cal" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    </div>

    <!-- Dropdown -->
    <div v-if="isOpen" class="dropdown">
      <!-- Month navigation -->
      <div class="cal-nav">
        <button class="nav-btn" type="button" @click="shiftMonth(-1)">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span class="month-title">{{ monthTitle }}</span>
        <button class="nav-btn" type="button" @click="shiftMonth(1)">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      <!-- Day-of-week headers -->
      <div class="dow-row">
        <span v-for="d in DOW_LABELS" :key="d" class="dow-cell">{{ d }}</span>
      </div>

      <!-- Week rows -->
      <div class="weeks">
        <div
          v-for="(week, wi) in calendarWeeks"
          :key="wi"
          class="week-row"
          :class="{
            'is-selected': isSelected(week[0]),
            'is-hovered': hoveredWeekIndex === wi && !isSelected(week[0])
          }"
          @mouseenter="hoveredWeekIndex = wi"
          @mouseleave="hoveredWeekIndex = null"
          @click="pick(week[0])"
        >
          <div
            v-for="(day, di) in week"
            :key="di"
            class="day-cell"
            :class="{
              'is-outside': !sameMonth(day),
              'is-today': isToday(day),
              'is-mon': di === 0,
              'is-sun': di === 6
            }"
          >
            <span class="day-num">{{ day.getDate() }}</span>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="footer">
        <button class="today-btn" type="button" @click="goToday">Сегодня</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CWeekPicker',

  directives: {
    clickOutside: {
      bind(el, binding) {
        el._clickOutsideHandler = (event) => {
          if (!el.contains(event.target)) {
            binding.value()
          }
        }
        document.addEventListener('click', el._clickOutsideHandler)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutsideHandler)
      },
    },
  },

  props: {
    // { from: Date, to: Date } | null
    value: {
      type: Object,
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Выберите неделю',
    },
    goTodayDefault: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOpen: false,
      view: this._getMonthStart(new Date()),
      selectedMon: null,
      hoveredWeekIndex: null,
      DOW_LABELS: [ 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс', ],
      MONTH_NAMES: [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
      ],
    }
  },

  computed: {
    selectedSun() {
      if (!this.selectedMon) return null
      const d = new Date(this.selectedMon)
      d.setDate(d.getDate() + 6)
      return d
    },

    monthTitle() {
      return `${this.MONTH_NAMES[this.view.getMonth()]} ${this.view.getFullYear()}`
    },

    calendarWeeks() {
      const y = this.view.getFullYear()
      const m = this.view.getMonth()
      const start = this._toMonday(new Date(y, m, 1))
      const cursor = new Date(start)
      const weeks = []
      for (let w = 0; w < 6; w++) {
        const week = []
        for (let d = 0; d < 7; d++) {
          week.push(new Date(cursor))
          cursor.setDate(cursor.getDate() + 1)
        }
        weeks.push(week)
      }
      return weeks
    },

    triggerLabel() {
      if (!this.selectedMon) return ''
      return `${this.formatDate(this.selectedMon)} – ${this.formatDate(this.selectedSun)}`
    },
  },

  watch: {
    value(val) {
      this.syncFromProp(val)
    },
  },

  created() {
    if (this.value && this.value.from) {
      this.syncFromProp(this.value)
    }
    if (this.goTodayDefault) {
      this.goToday()
    }
  },

  methods: {

    // ── Helpers ────────────────────────────────────────────
    _midnight(d) {
      const c = new Date(d)
      c.setHours(0, 0, 0, 0)
      return c
    },

    _getMonthStart(d) {
      return new Date(d.getFullYear(), d.getMonth(), 1)
    },

    _toMonday(d) {
      const c = this._midnight(d)
      const dow = c.getDay()
      const delta = dow === 0 ? -6 : 1 - dow
      c.setDate(c.getDate() + delta)
      return c
    },

    _sameDay(a, b) {
      return (
        a.getFullYear() === b.getFullYear() &&
          a.getMonth() === b.getMonth() &&
          a.getDate() === b.getDate()
      )
    },

    // ── Calendar ───────────────────────────────────────────
    sameMonth(d) {
      return (
        d.getMonth() === this.view.getMonth() &&
          d.getFullYear() === this.view.getFullYear()
      )
    },

    isToday(d) {
      return this._sameDay(d, new Date())
    },

    isSelected(mon) {
      return !!this.selectedMon && this._sameDay(mon, this.selectedMon)
    },

    pick(monday) {
      this.selectedMon = new Date(monday)
      const payload = { from: this.selectedMon, to: this.selectedSun, }
      this.$emit('input', payload)
      this.$emit('change', payload)
      this.close()
    },

    shiftMonth(delta) {
      const d = new Date(this.view)
      d.setMonth(d.getMonth() + delta)
      this.view = d
    },

    goToday() {
      const mon = this._toMonday(new Date())
      this.view = this._getMonthStart(mon)
      this.pick(mon)
    },

    formatDate(d) {
      if (!d) return '—'
      return d.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },

    syncFromProp(val) {
      if (val && val.from) {
        const mon = this._toMonday(val.from)
        this.selectedMon = mon
        this.view = this._getMonthStart(mon)
      }
    },

    // ── Dropdown ───────────────────────────────────────────
    toggle() {
      this.isOpen = !this.isOpen
    },

    close() {
      this.isOpen = false
    },
  },
}
</script>

<style lang="scss" scoped>
$green: #1D9E75;
$border: #e0e0e0;
$radius: 12px;
$text: #222;
$muted: #888;

.c-week-picker {
  position: relative;
  width: 100%;
  max-width: 260px;
  font-size: 16px;
  font-family: inherit;
}

/* ── Trigger ── */
.trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1.5px solid $border;
  border-radius: $radius;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.2s;
  gap: 8px;

  &.is-open,
  &:hover {
    border-color: $green;
  }

  &.has-value .trigger-text {
    color: $text;
  }

  .trigger-text {
    flex: 1;
    color: $muted;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .icon-cal {
    flex-shrink: 0;
    color: $muted;
  }
}

/* ── Dropdown ── */
.dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 100;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: $radius;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10);
  overflow: hidden;
  min-width: 260px;
}

/* ── Calendar nav ── */
.cal-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid $border;

  .nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    border-radius: 7px;
    cursor: pointer;
    color: $muted;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: #f6f6f6;
      color: $text;
    }
  }

  .month-title {
    font-size: 14px;
    font-weight: 600;
    color: $text;
  }
}

/* ── Day-of-week headers ── */
.dow-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 8px 12px 4px;

  .dow-cell {
    text-align: center;
    font-size: 10px;
    font-weight: 700;
    color: darken($border, 20%);
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }
}

/* ── Weeks grid ── */
.weeks {
  padding: 4px 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.week-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;

  &.is-hovered {
    background: #f6f6f6;
  }

  &.is-selected {
    background: rgba($green, 0.09);
  }
}

/* ── Day cells ── */
.day-cell {
  display: flex;
  align-items: center;
  justify-content: center;

  &.is-outside .day-num {
    color: lighten($border, 5%);
  }

  &.is-today:not(.is-outside) .day-num {
    color: $green;
    font-weight: 700;
  }
}

.day-num {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 400;
  color: $text;
  transition: background 0.12s, color 0.12s;
}

/* ── Selected week overrides ── */
.week-row.is-selected {
  .day-cell {
    .day-num {
      color: darken($green, 8%);
      font-weight: 500;
    }

    &.is-mon .day-num,
    &.is-sun .day-num {
      background: $green;
      color: #fff;
      font-weight: 600;
    }

    &.is-today:not(.is-mon):not(.is-sun) .day-num {
      background: rgba($green, 0.18);
      color: darken($green, 10%);
    }

    &.is-today.is-mon .day-num,
    &.is-today.is-sun .day-num {
      background: darken($green, 8%);
      color: #fff;
    }
  }
}

/* ── Footer ── */
.footer {
  padding: 10px 16px;
  border-top: 1px solid $border;
}

.today-btn {
  background: none;
  border: none;
  color: $green;
  font-size: 15px;
  cursor: pointer;
  padding: 0;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }
}
</style>
